/**
 * 知识条目。一个 Markdown 文件对应一个 Concept，题目挂在它下面。
 * Concept 只有内存形状，没有落盘形状；每次 reload 从文件重建，不在进程内增量修改。
 * 依赖 ids 与 question，被 parsing 与 library 构造，被 plan、workspace 与屏幕读形状。
 * 不解析 Markdown，不判分，也不持有任何复习状态。
 */

import type { CardRef, ConceptId, EpochDay, RepoPath } from './ids'
import { conceptCard } from './ids'
import type { AnyQuestion } from './question'

/** 作者预估难度。与调度算出来的 ease 是两条独立轴，系统不合并它们。 */
export type Difficulty = 1 | 2 | 3 | 4 | 5

/** 标签在解析时已过滤字符集，这里不再校验。 */
export type Tag = string

/**
 * 正文段落，按 `##` 与 `###` 切开。段落是 Concept 唯一保存的正文形式，
 * 全文由 sections 顺序拼接得到，所以不存在「段落」和「全文」两份需要同步的数据。
 */
export interface Section {
	/** 上级标题指向的段落文本，不含题块。第一个标题之前的段落 heading 为 null。 */
	readonly heading: string | null
	readonly level: 2 | 3
	readonly markdown: string
	readonly sourceLine: number
}

export interface Concept {
	readonly id: ConceptId
	/** 展示与错误定位用，不参与身份。 */
	readonly path: RepoPath
	readonly title: string
	readonly tags: readonly Tag[]
	readonly difficulty: Difficulty
	/** 仅作展示。调度不读它，缺失时为 null，不补 git 历史。 */
	readonly createdOn: EpochDay | null
	readonly aliases: readonly string[]
	readonly sections: readonly [Section, ...Section[]]
	/** 题目真源。按文件里的书写顺序排列，库不给它们重新排序。 */
	readonly questions: readonly AnyQuestion[]
}

/**
 * 派生知识点卡。作者不写、仓库里没有它，内容全从 Concept 算出来，所以它不是第二份真源。
 * 身份是 conceptCard(conceptId)，即日志里的 `c:` 卡；这张卡只承载复述自评。
 */
export interface RecallCard {
	readonly card: CardRef
	/** title 加上各 `##` 小标题组成的要点清单，就是这道复述题的题干。 */
	readonly prompt: string
	readonly headings: readonly string[]
}

export function recallCardOf(concept: Concept): RecallCard {
	const headings: string[] = []
	for (const section of concept.sections) {
		if (section.level === 2 && section.heading !== null) headings.push(section.heading)
	}
	const bullets = headings.map((heading) => `- ${heading}`).join('\n')
	return {
		card: conceptCard(concept.id),
		prompt: bullets === '' ? concept.title : `${concept.title}\n\n${bullets}`,
		headings,
	}
}

/**
 * 某个段落下的题目。派生查询，不存字段，所以段落和题目的归属关系只有一处定义。
 * 不变量：question.sectionIndex 一定落在 sections 范围内，由 parsing 保证。
 */
export function questionsIn(concept: Concept, sectionIndex: number): readonly AnyQuestion[] {
	return concept.questions.filter((question) => question.sectionIndex === sectionIndex)
}

/** 信任边界：parsing 已保证 index 合法，这里不再判断越界。 */
export function sectionOf(concept: Concept, index: number): Section {
	return concept.sections[index] as Section
}

/** 条目正文里是否至少有一道作者题。没有题的条目仍然可以阅读，知识点卡也照样能复习。 */
export function hasAuthorQuestions(concept: Concept): boolean {
	return concept.questions.length > 0
}
